/* ==========================================================================
   Step 3 — Submit order

   One card per shipment: what is in it, what it comes to, and one button that
   loads that shipment into the trade cart and takes the buyer to checkout.

   WHY ONE SHIPMENT AT A TIME
   --------------------------
   SparkLayer has exactly one cart, and its ship date, cancel date and PO are
   cart-level fields. Three dated drops with three POs cannot be one cart, so
   each shipment is checked out in turn and becomes its own order. That is not
   a UI choice we could undo by trying harder — it is the shape of the data.

   What this page does is make that honest: the buyer sees all three, submits
   one, comes back and submits the next, and each card says where it got to.

   Loading a shipment CLEARS the trade cart first. With one cart there is no
   way to hold two shipments at once, and a half-mixed cart would check out as
   one wrong order. The buyer is told before it happens.

   Quantities come from the workbook, where step 2 now saves them as they are
   typed (shipment.qty = { sku: units }). The cart is not touched until here.
   ========================================================================== */
(function () {
  'use strict';

  function money(amount, currency, locale) {
    try {
      return new Intl.NumberFormat(locale || 'en-US', {
        style: 'currency', currency: currency || 'USD'
      }).format(amount);
    } catch (e) {
      return '$' + Number(amount).toFixed(2);
    }
  }

  function readJson(el, fallback) {
    if (!el) return fallback;
    try {
      var v = JSON.parse(el.textContent);
      return v == null ? fallback : v;
    } catch (e) {
      return fallback;
    }
  }

  function Submit(root) {
    this.root = root;
    this.customerId = root.dataset.customerId || '';
    this.currency = root.dataset.currency || 'USD';
    this.locale = root.dataset.locale || 'en-US';
    this.builderUrl = root.dataset.builderUrl || '';
    this.quantitiesUrl = root.dataset.quantitiesUrl || '';
    this.cartUrl = root.dataset.cartUrl || '/cart';

    this.list = root.querySelector('[data-b2bsub-list]');
    this.status = root.querySelector('[data-b2bsub-status]');

    // SKU -> { p: parent product id, t: title, s: size }. Rendered by Liquid
    // from the same collection the other two steps use, so a shipment can be
    // priced and described here without the catalogue being on the page.
    this.skus = readJson(root.querySelector('[data-b2bsub-skus]'), {});
    this.orders = readJson(root.querySelector('[data-b2bsub-orders]'), []);

    this.wb = window.B2BWorkbook.load(this.customerId);
    this.prices = {};

    this.reconcileOrders();
    this.render();
    this.initPricing();
    this.bind();
  }

  /* A submitted shipment is only called ordered when a real order backs it —
     same rule as the Build Order page. Duplicated rather than shared because
     the Builder keeps it on its prototype; worth consolidating if a third
     page ever needs it. */
  Submit.prototype.matchOrder = function (s) {
    if (!s.submittedAt || !s.submittedSkus || !s.submittedSkus.length) return null;
    if (!this.orders.length) return null;

    var since = (Date.parse(s.submittedAt) || 0) / 1000 - 60;
    var seen = {};
    var newest = null;

    this.orders.forEach(function (o) {
      if (!o || typeof o.at !== 'number' || o.at < since) return;
      (o.skus || []).forEach(function (sku) { if (sku) seen[String(sku)] = true; });
      if (!newest || o.at > newest.at) newest = o;
    });
    if (!newest) return null;

    return s.submittedSkus.every(function (sku) { return !!seen[sku]; }) ? newest : null;
  };

  Submit.prototype.reconcileOrders = function () {
    var self = this;
    var changed = false;

    this.wb.shipments.forEach(function (s) {
      if (s.orderedAt) return;
      var o = self.matchOrder(s);
      if (!o) return;
      s.orderedAt = new Date((o.at || 0) * 1000).toISOString();
      s.orderName = o.name || '';
      changed = true;
    });

    if (changed) this.persist();
  };

  Submit.prototype.persist = function () {
    if (this.builderUrl) this.wb.builderUrl = this.builderUrl;
    window.B2BWorkbook.save(this.customerId, this.wb);
  };

  /* ---------- what is in a shipment ---------- */

  Submit.prototype.lines = function (s) {
    var self = this;
    var qty = s.qty || {};
    var out = [];
    Object.keys(qty).forEach(function (sku) {
      var n = qty[sku];
      if (!n || n < 1) return;
      var meta = self.skus[sku] || {};
      out.push({ sku: sku, units: n, title: meta.t || sku, size: meta.s || '', parent: meta.p || '' });
    });
    return out;
  };

  Submit.prototype.totals = function (s) {
    var self = this;
    var lines = this.lines(s);
    var units = 0;
    var value = 0;
    var priced = true;
    var styles = {};

    lines.forEach(function (l) {
      units += l.units;
      styles[l.title] = true;
      var p = self.prices[l.sku];
      if (typeof p === 'number') value += p * l.units;
      else priced = false;
    });

    return {
      lines: lines.length,
      units: units,
      styles: Object.keys(styles).length,
      value: value,
      priced: priced && lines.length > 0
    };
  };

  /* ---------- rendering ---------- */

  Submit.prototype.render = function () {
    var self = this;
    if (!this.list) return;
    this.list.innerHTML = '';

    if (!this.wb.shipments.length) {
      this.list.appendChild(this.emptyCard());
      return;
    }

    this.wb.shipments.forEach(function (s) {
      self.list.appendChild(self.card(s));
    });
  };

  Submit.prototype.emptyCard = function () {
    var p = document.createElement('p');
    p.className = 'b2bsub__empty';
    p.textContent = 'No shipments yet. Start at step 1 and pick the styles you want.';
    return p;
  };

  Submit.prototype.card = function (s) {
    var t = this.totals(s);
    var ordered = !!s.orderedAt;
    var inCart = !ordered && !!s.submittedAt;
    var ready = t.units > 0;

    var card = document.createElement('article');
    card.className = 'b2bsub-card' +
      (ordered ? ' is-ordered' : '') +
      (inCart ? ' is-sent' : '') +
      (!ready && !ordered ? ' is-empty' : '');
    card.setAttribute('data-b2bsub-card', s.id);

    /* head — name and state */
    var head = document.createElement('div');
    head.className = 'b2bsub-card__head';

    var name = document.createElement('h2');
    name.className = 'b2bsub-card__name';
    name.textContent = s.name;
    head.appendChild(name);

    var state = document.createElement('span');
    state.className = 'b2bsub-card__state';
    state.textContent = ordered
      ? (s.orderName ? 'Ordered · ' + s.orderName : 'Ordered')
      : inCart ? 'In your cart'
      : ready ? 'Ready to submit'
      : 'No quantities yet';
    head.appendChild(state);
    card.appendChild(head);

    /* facts — dates, PO, what it comes to. Each is a label-over-value pair in
       its own box so the row wraps cleanly rather than needing a grid that
       has to be unpicked again on a phone. */
    var facts = document.createElement('div');
    facts.className = 'b2bsub-card__facts';

    var fact = function (label, value, opts) {
      var box = document.createElement('div');
      box.className = 'b2bsub-fact';
      var l = document.createElement('span');
      l.className = 'b2bsub-fact__label';
      l.textContent = label;
      var v = document.createElement('span');
      v.className = 'b2bsub-fact__value' + ((opts && opts.strong) ? ' is-strong' : '');
      v.textContent = value;
      if (opts && opts.attr) v.setAttribute(opts.attr[0], opts.attr[1]);
      box.appendChild(l);
      box.appendChild(v);
      return box;
    };

    facts.appendChild(fact('Start ship', s.shipDate || '—'));
    facts.appendChild(fact('Cancel', s.cancelDate || '—'));
    facts.appendChild(fact('PO', s.po || '—'));
    facts.appendChild(fact('Units', ready ? String(t.units) : '0'));
    facts.appendChild(fact(
      'Total',
      t.priced ? money(t.value, this.currency, this.locale) : '—',
      { strong: true, attr: ['data-b2bsub-cost', s.id] }
    ));

    card.appendChild(facts);

    /* what is in it */
    if (ready) {
      var summary = document.createElement('p');
      summary.className = 'b2bsub-card__summary';
      summary.textContent = t.styles + (t.styles === 1 ? ' style' : ' styles') +
        ' · ' + t.lines + (t.lines === 1 ? ' size' : ' sizes');
      card.appendChild(summary);
    }

    /* actions */
    var actions = document.createElement('div');
    actions.className = 'b2bsub-card__actions';

    if (ordered) {
      var done = document.createElement('span');
      done.className = 'b2bsub-card__done';
      done.textContent = s.orderName
        ? 'Placed as order ' + s.orderName
        : 'Placed';
      actions.appendChild(done);
    } else {
      var go = document.createElement('button');
      go.type = 'button';
      go.className = 'b2bsub-btn b2bsub-btn--primary';
      go.setAttribute('data-b2bsub-go', s.id);
      go.textContent = inCart ? 'Back to checkout' : 'Submit ' + s.name;
      go.disabled = !ready;
      actions.appendChild(go);

      if (this.quantitiesUrl) {
        var edit = document.createElement('a');
        edit.className = 'b2bsub-btn';
        edit.href = this.quantitiesUrl +
          (this.quantitiesUrl.indexOf('?') === -1 ? '?' : '&') +
          'shipment=' + encodeURIComponent(s.id);
        edit.textContent = ready ? 'Edit quantities' : 'Add quantities';
        actions.appendChild(edit);
      }
    }

    card.appendChild(actions);
    return card;
  };

  Submit.prototype.say = function (text, isError) {
    if (!this.status) return;
    this.status.textContent = text || '';
    this.status.hidden = !text;
    this.status.classList.toggle('is-error', !!isError);
  };

  /* ---------- pricing ----------
     Same engine as the other two steps, so a style costs the same figure
     everywhere. One call per SKU in the workbook, not per catalogue product,
     so this is small however big the catalogue is. No RRP fallback: an
     unpriced total shows as a dash rather than a retail figure. */

  Submit.prototype.initPricing = function () {
    var self = this;

    var wanted = {};
    this.wb.shipments.forEach(function (s) {
      self.lines(s).forEach(function (l) { if (l.parent) wanted[l.sku] = l.parent; });
    });
    var skus = Object.keys(wanted);
    if (!skus.length) return;

    var tries = 0;
    var attempt = function () {
      if (!window.spark || typeof window.spark.calculatePricingForVariant !== 'function') {
        if (tries++ > 40) return;
        return void setTimeout(attempt, 500);
      }

      var queue = skus.slice();
      var next = function () {
        var sku = queue.shift();
        if (!sku) return Promise.resolve();
        return Promise.resolve()
          .then(function () { return window.spark.calculatePricingForVariant(wanted[sku], sku, 1); })
          .then(function (res) {
            if (res && typeof res.unitPrice === 'number') {
              self.prices[sku] = res.unitPrice;
              if (res.currencyCode) self.currency = res.currencyCode.toUpperCase();
            }
          })
          .catch(function () { /* leave it unpriced rather than guess */ })
          .then(next);
      };

      var workers = [];
      for (var w = 0; w < 4; w++) workers.push(next());
      return Promise.all(workers).then(function () { self.refreshTotals(); });
    };

    attempt();
  };

  // Only the money is redrawn: re-rendering the cards would throw away focus
  // and any button mid-press.
  Submit.prototype.refreshTotals = function () {
    var self = this;
    this.wb.shipments.forEach(function (s) {
      var el = self.root.querySelector('[data-b2bsub-cost="' + s.id + '"]');
      if (!el) return;
      var t = self.totals(s);
      el.textContent = t.priced ? money(t.value, self.currency, self.locale) : '—';
    });
  };

  /* ---------- submitting one shipment ---------- */

  Submit.prototype.bind = function () {
    var self = this;
    this.root.addEventListener('click', function (e) {
      var go = e.target.closest('[data-b2bsub-go]');
      if (!go) return;
      self.submit(go.getAttribute('data-b2bsub-go'), go);
    });
  };

  Submit.prototype.submit = function (id, btn) {
    var self = this;
    var s = window.B2BWorkbook.find(this.wb, id);
    if (!s) return;

    if (!window.spark || typeof window.spark.updateCart !== 'function') {
      return this.say('The trade cart is still loading — try again in a moment.', true);
    }

    var lines = this.lines(s);
    if (!lines.length) return this.say('Add some quantities to ' + s.name + ' first.', true);

    /* The cart holds one shipment at a time, so say so plainly rather than
       silently discarding whatever is in there. */
    var others = this.wb.shipments.filter(function (o) {
      return o.id !== s.id && o.submittedAt && !o.orderedAt;
    });
    var warn = others.length
      ? '\n\nThis will take ' + others.map(function (o) { return o.name; }).join(' and ') +
        ' back out of your cart — you can submit ' +
        (others.length === 1 ? 'it' : 'them') + ' again afterwards.'
      : '';

    if (!window.confirm('Load ' + s.name + ' into your cart and go to checkout?' + warn)) return;

    var original = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Loading…';
    this.say('');

    var products = lines.map(function (l) { return { sku: l.sku, quantity: l.units }; });
    var customFields = [
      { name: 'shipping-date', value: s.shipDate || '' },
      { name: 'cancel-date', value: s.cancelDate || '' },
      { name: 'po-number', value: s.po || '' }
    ];

    // Clear, then load: one call each, in order, because the clear must land
    // before the lines or it would wipe what we just added.
    Promise.resolve(window.spark.updateCart({ clearCart: true }, true))
      .then(function () {
        return window.spark.updateCart({ products: products, customFields: customFields }, true);
      })
      .then(function (res) {
        if (res === null) throw new Error('updateCart returned null');

        self.wb.shipments.forEach(function (o) {
          if (o.id === s.id || o.orderedAt) return;
          // Anything else that was in the cart no longer is.
          o.submittedAt = null;
          o.submittedSkus = [];
        });
        s.submittedAt = new Date().toISOString();
        s.submittedSkus = products.map(function (p) { return p.sku; });
        self.persist();
        self.render();

        self.say(s.name + ' is in your cart. Taking you to checkout…');
        setTimeout(function () { self.openCart(); }, 600);
      })
      .catch(function () {
        btn.disabled = false;
        btn.textContent = original;
        self.say('Could not load ' + s.name + ' into your cart. Please try again.', true);
      });
  };

  Submit.prototype.openCart = function () {
    if (window.spark && typeof window.spark.openDrawer === 'function') {
      try { return window.spark.openDrawer('cart'); } catch (e) { /* fall through */ }
    }
    window.location.href = this.cartUrl;
  };

  /* ---------- boot ---------- */

  function init() {
    var root = document.querySelector('[data-b2bsub-root]');
    if (!root || root.dataset.b2bsubReady === '1') return;
    if (!window.B2BWorkbook) return;
    root.dataset.b2bsubReady = '1';
    new Submit(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();